/**
 * Common utilities for minting songs and stems.
 */

import {
  ArpAccess,
  dedupe,
  getPrimaryInstrumentFromMedia,
  InternalArpMetadata,
  StemType
} from '@arpeggi/shared'
import ArpRegistry from '../../features/arp-registry/ArpRegistry'
import { UploadSoundModalForm } from '../components/studio/mint/upload-sound-modal/MintSoundUploadPage'
import { getStemDisplayName } from '../components/studio/StudioUtils'
import { ISongStore } from '../stores/song/SongStore'
import { IAudioStemStore } from '../stores/song/stem/AudioStemStore'
import { IDrumStemStore } from '../stores/song/stem/DrumStemStore'
import { IInstrumentStemStore } from '../stores/song/stem/InstrumentStemStore'
import { IStemStore } from '../stores/song/StemStore'
import { IStudioStore } from '../stores/StudioStore'

/**
 * Gets the correctly formatted metadata object for the given song
 */
export const getMetadataForSong = (song: ISongStore, genres?: string[]) => {
  // Construct this format to match the way data is stored on the contract.
  const metadata: InternalArpMetadata = {
    title: song.name,
    mediaType: 'song',
    tags: [],
    genres: genres || [],
    key: song.key.toInt(),
    bpm: song.tempo,
    access: ArpAccess.STANDARD
    // TODO, clear the stems, since we've already uploaded them elsewhere. Can use the subcomponent indices to reconstruct the song.
    //  Whenever we reconstruct the song, repopulate the stems from the DB.
    // songClone.stems.clear()
  }
  return metadata
}

/**
 * Calculates the subcomponents of the stem.
 */
export const getSubcomponentsForStem = (studio: IStudioStore, stem: IStemStore): Array<number> => {
  const sampleSubcomponents = []
  if (stem.type() === StemType.DRUMS) {
    const drumStem = stem as IDrumStemStore
    drumStem.drumSamples.forEach((sample) => {
      sampleSubcomponents.push(sample.sampleIndex)
    })
  } else if (stem.type() === StemType.INSTRUMENT) {
    sampleSubcomponents.push((stem as IInstrumentStemStore).melodicSample.sampleIndex)
  } else if (stem.type() === StemType.AUDIO) {
    const audioStem = stem as IAudioStemStore
    audioStem.clips.forEach((clip) => sampleSubcomponents.push(clip.sampleWithEffects.sampleIndex))
  }
  return dedupe(sampleSubcomponents)
}

/**
 * Gets the correctly formatted metadata object for the given stem.
 */
export const getMetadataForStem = (
  studio: IStudioStore,
  stem: IStemStore,
  song: ISongStore,
  genres?: string[]
) => {
  const songName = song.name ? song.name : 'Untitled'

  const categories = []
  if (stem.type() === StemType.DRUMS) {
    categories.push('Drums')
  } else if (stem.type() === StemType.INSTRUMENT) {
    categories.push(
      ArpRegistry.retrieveFromCache((stem as IInstrumentStemStore).getMelodicSample())?.category
    )
  } else if (stem.type() === StemType.AUDIO) {
    const category = getInstrumentForAudioStem(stem as IAudioStemStore)
    if (category) {
      categories.push(category)
    } else {
      categories.push('Audio')
    }
  }

  // Construct this format to match the way data is stored on the contract.
  const metadata: InternalArpMetadata = {
    title: getStemDisplayName(stem) + ' - ' + songName,
    mediaType: 'stem',
    tags: categories,
    genres: genres || [],
    key: stem.type() === StemType.DRUMS ? 0 : song.key.toInt(),
    bpm: song.tempo,
    access: ArpAccess.STANDARD // default to standard
  }
  return metadata
}

/**
 * Gets the correctly formatted metadata object for the given sound.
 */
export const getMetadataForSound = (
  uploadSoundData: UploadSoundModalForm,
  customTags: string[]
) => {
  const tags = [uploadSoundData.instrument].concat(customTags)
  const metadata: InternalArpMetadata = {
    title: uploadSoundData.sampleTitle,
    mediaType: uploadSoundData.soundType,
    tags: tags,
    genres: uploadSoundData.genres || [],
    key: uploadSoundData.key || 0,
    bpm: uploadSoundData.bpm || 0,
    access: ArpAccess.STANDARD
  }
  return metadata
}

/** Gets the most frequent instrument of all clips in the audio stem, or undefined if none. */
const getInstrumentForAudioStem = (stem: IAudioStemStore): string => {
  // Count each instrument of each clip
  let map = new Map()
  stem.clips.forEach((clip) => {
    const sample = clip.sampleWithEffects.getArpMedia()
    if (sample) {
      const instrument = getPrimaryInstrumentFromMedia(sample)
      const currValue = map.get(instrument) || 0
      map.set(instrument, currValue + 1)
    }
  })
  // Use most common instrument
  let mostCommonInstrument = undefined
  let mostCommonCount = 0
  map.forEach((count, instrument) => {
    if (count > mostCommonCount) {
      mostCommonCount = count
      mostCommonInstrument = instrument
    }
  })
  return mostCommonInstrument
}
